export type AppEnv = 'local' | 'dev' | 'int' | 'uat' | 'prod'

const APP_ENVS: readonly string[] = ['local', 'dev', 'int', 'uat', 'prod'] satisfies AppEnv[]

// Anything missing or unrecognised is treated as non-productive, never as prod.
const FALLBACK_ENV: AppEnv = 'dev'

function isAppEnv(value: unknown): value is AppEnv {
  return typeof value === 'string' && APP_ENVS.includes(value)
}

/** The environment the app runs in, from the runtime config.js written at container start. */
export function getAppEnv(): AppEnv {
  const environment = window.__APP_CONFIG__?.environment
  return isAppEnv(environment) ? environment : FALLBACK_ENV
}

/** What the environment banner shows on a non-productive environment. */
export type EnvironmentBannerMeta = {
  label: string
  description: string
  /** Tells the environments apart at a glance, not only by reading. */
  colorPalette: 'gray' | 'purple' | 'blue' | 'orange'
}

// Production deliberately has no entry: no entry, no banner. Every other environment must have
// one, so a new AppEnv does not compile until its banner text exists.
export const ENV_BANNERS: Readonly<Record<Exclude<AppEnv, 'prod'>, EnvironmentBannerMeta>> = {
  // Proposed wording: SIB-932 leaves the local text open.
  local: {
    label: 'LOCAL Umgebung',
    description:
      'Lokale Entwicklungsumgebung. Diese Anwendung läuft lokal. Die angezeigten Daten sind keine Produktionsdaten.',
    colorPalette: 'gray',
  },
  dev: {
    label: 'DEV Umgebung',
    description:
      'Entwicklungsumgebung. Hier wird aktiv entwickelt: Stände können jederzeit ohne Ankündigung ausgetauscht oder zurückgesetzt werden. Alle Daten sind Testdaten.',
    colorPalette: 'purple',
  },
  // INT and UAT wording and the int/uat split still await confirmation (SIB-932).
  int: {
    label: 'INT Umgebung',
    description:
      'Integrationsumgebung. Hier prüft Polygran neue Stände technisch, bevor sie zur Abnahme gehen. Funktionen können unfertig sein, alle Daten sind Testdaten und keine Produktionsdaten.',
    colorPalette: 'blue',
  },
  uat: {
    label: 'UAT Umgebung',
    description:
      'Abnahmeumgebung. Hier werden neue Funktionen fachlich geprüft und abgenommen. Der Stand entspricht dem geplanten Release, alle Daten sind jedoch Testdaten und keine Produktionsdaten.',
    colorPalette: 'orange',
  },
}

/** The banner for the current environment, or undefined where none is shown (production). */
export function getEnvironmentBannerMeta(): EnvironmentBannerMeta | undefined {
  const banners: Partial<Record<AppEnv, EnvironmentBannerMeta>> = ENV_BANNERS
  return banners[getAppEnv()]
}
